// WHAT: Streak Display Component (Molecule)
// WHY:  Displays current streak count with motivational messaging
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: UI Card, Stack, Heading components, Lucide icons
// RISKS:
//   - NOTE: Motivational message is hardcoded (not personalized)
//   - NOTE: Streak value is displayed as-is (no validation or formatting)

import { Flame, TrendingUp } from "lucide-react";
import React from "react";

import { Card, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

interface StreakDisplayProperties {
  className?: string;
  streak: number;
}

/**
 * WHAT:
 *   Card component displaying current streak count with visual emphasis.
 *
 * WHY:
 *   Provides prominent visual feedback for user's consecutive session completion.
 *
 * PROPS:
 *   - streak: number — Current streak count in days
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays streak number in large font (6xl) with "days" label
 *   - Shows flame icon in gradient background
 *   - Displays motivational message with trending up icon
 *   - Uses animated background glow effect (pulse animation)
 *   - Card has hover effects (border color, shadow)
 *
 * RISKS:
 *   - NOTE: Streak value is displayed as-is (may show negative or invalid values)
 *   - NOTE: Motivational message is hardcoded (not personalized to streak length)
 *   - NOTE: Animation may cause performance issues on low-end devices
 *
 * @param root0
 *
 * @param root0.streak
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const StreakDisplay: React.FC<StreakDisplayProperties> = ({ className, streak }) => {
  return (
    <Card
      className={cn(
        "relative overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-[#334155]",
        "hover:border-tracq-bright-blue/50 transition-all duration-300",
        "hover:shadow-tracq-card",
        className,
      )}
      padding="lg"
      variant="default"
    >
      {/* Animated Background Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-tracq-deep-blue/20 via-tracq-bright-blue/10 to-transparent opacity-50 animate-pulse" />

      <Stack className="relative z-10" direction="col" gap={4}>
        <Stack align="center" direction="row" gap={3}>
          <div className="flex items-center justify-center w-16 h-16 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
            <Flame className="w-8 h-8 text-white" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading
              className="text-[#94A3B8] font-medium uppercase tracking-wider text-xs"
              level="h3"
              size="md"
            >
              Current Streak
            </Heading>
            <div className="flex items-baseline gap-2">
              <span className="text-6xl font-bold text-[#F1F5F9] leading-none">{streak}</span>
              <span className="text-xl text-[#94A3B8] font-medium">days</span>
            </div>
          </Stack>
        </Stack>

        <div className="flex items-center gap-2 text-[#94A3B8] text-sm">
          <TrendingUp className="w-4 h-4 text-tracq-bright-blue" />
          <span>Keep it going! You&apos;re on fire!</span>
        </div>
      </Stack>
    </Card>
  );
};
